import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, MapPin } from 'lucide-react'

import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { AttendanceSheet } from '@/features/groups/attendance-sheet'
import { GROUP_PERMISSIONS, useGroupPermissions } from '@/features/groups/permissions'
import { useGroup, useGroupMeeting } from '@/features/groups/queries'
import { formatTimeOfDay } from '@/features/groups/constants'
import { formatShortDate } from '@/lib/datetime'

/**
 * Recording attendance for one meeting.
 *
 * The meeting is looked up in the group's own calendar because there is no endpoint that reads a
 * single meeting, and everything else this screen needs — who was a member on that date, where the
 * group meets — comes from the group anyway.
 *
 * This screen does not refuse a narrow display. The argument is in docs/mobile-policy.md; the
 * short version is that every input here is a choice among four rather than a keyed amount, and
 * the one thing a phone genuinely cannot do — show eighteen rows at once before they commit — is
 * answered by the confirmation, which states the totals back.
 */
export function MeetingAttendancePage() {
  const { groupId, meetingId } = useParams({
    from: '/authenticated/groups/$groupId/meetings/$meetingId',
  })
  const navigate = useNavigate()
  const permissions = useGroupPermissions()

  const group = useGroup(groupId)
  const meetings = useGroupMeeting(groupId, meetingId)

  const meeting = meetings.data

  const backToGroup = (
    <Link
      to="/groups/$groupId"
      params={{ groupId }}
      search={{ tab: 'meetings' }}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-lg:min-h-11 max-lg:py-2"
    >
      <ArrowLeft className="size-3.5" aria-hidden />
      Back to the group
    </Link>
  )

  if (!permissions.canRecordAttendance) {
    return (
      <>
        {backToGroup}
        <PageHeader title="Attendance" />
        <Alert tone="warning">
          <AlertTitle>Recording attendance is restricted</AlertTitle>
          <AlertDescription>
            It needs the <span className="font-mono text-xs">{GROUP_PERMISSIONS.meetingRecord}</span>{' '}
            permission. The register for a meeting that has already been recorded is readable on the
            group&apos;s meetings tab.
          </AlertDescription>
        </Alert>
      </>
    )
  }

  if (group.isPending || meetings.isPending) {
    return (
      <>
        {backToGroup}
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </>
    )
  }

  if (group.isError) {
    return (
      <>
        {backToGroup}
        <ErrorSurface
          error={group.error}
          onRetry={() => void group.refetch()}
          title="This group could not be opened"
        />
      </>
    )
  }

  if (meetings.isError) {
    return (
      <>
        {backToGroup}
        <ErrorSurface
          error={meetings.error}
          onRetry={() => void meetings.refetch()}
          title="The meeting calendar could not be read"
        />
      </>
    )
  }

  if (!meeting) {
    return (
      <>
        {backToGroup}
        <PageHeader title="Attendance" />
        <Alert tone="warning">
          <AlertTitle>That meeting is not on this group&apos;s calendar</AlertTitle>
          <AlertDescription>
            It may have been cancelled by a schedule change, or the link may belong to another
            group.
          </AlertDescription>
        </Alert>
      </>
    )
  }

  const alreadyRecorded = meeting.status === 'Completed' || meeting.status === 'Missed'
  const cancelled = meeting.status === 'Cancelled'

  return (
    <>
      {backToGroup}

      <PageHeader
        title={`${group.data.name} — ${formatShortDate(meeting.scheduledDate)}`}
        reference={group.data.groupCode}
        badges={<StatusBadge status={meeting.status} />}
        description={`Round ${String(meeting.roundNumber)}, scheduled for ${formatTimeOfDay(meeting.scheduledTime)}.`}
      />

      {meeting.meetingLocationName ? (
        <p className="inline-flex items-center gap-1.5 text-xs text-muted">
          <MapPin className="size-3.5" aria-hidden />
          {meeting.meetingLocationName}
        </p>
      ) : null}

      {cancelled ? (
        <Alert tone="warning">
          <AlertTitle>This meeting was cancelled</AlertTitle>
          <AlertDescription>
            A schedule change cancels meetings the previous schedule had put on the calendar where
            they had not yet happened. There is nothing to record against it.
          </AlertDescription>
        </Alert>
      ) : alreadyRecorded ? (
        <Alert tone="info">
          <AlertTitle>This register has already been recorded</AlertTitle>
          <AlertDescription>
            The meeting is {meeting.status.toLowerCase()} and is closed to attendance. The register
            as recorded is on the group&apos;s meetings tab, with the check-in fix it was taken
            against — or the absence of one.
          </AlertDescription>
        </Alert>
      ) : (
        <AttendanceSheet
          group={group.data}
          meeting={meeting}
          onRecorded={() => {
            void navigate({
              to: '/groups/$groupId',
              params: { groupId },
              search: { tab: 'meetings' },
            })
          }}
        />
      )}
    </>
  )
}
